/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

/*
* When adding modules to the namespace, ensure that they are added in alphabetical order according to module name.
*/

// MODULES //

import setReadOnly from '@stdlib/utils/define-read-only-property';


// MAIN //

/**
* Top-level namespace.
*
* @namespace t
*/
var t = {};

/**
* @name cdf
* @memberof t
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/t/cdf}
*/
import cdf from './../../../../base/dists/t/cdf';
setReadOnly( t, 'cdf', cdf );

/**
* @name T
* @memberof t
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/t/ctor}
*/
import T from './../../../../base/dists/t/ctor';
setReadOnly( t, 'T', T );

/**
* @name entropy
* @memberof t
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/t/entropy}
*/
import entropy from './../../../../base/dists/t/entropy';
setReadOnly( t, 'entropy', entropy );

/**
* @name kurtosis
* @memberof t
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/t/kurtosis}
*/
import kurtosis from './../../../../base/dists/t/kurtosis';
setReadOnly( t, 'kurtosis', kurtosis );

/**
* @name logcdf
* @memberof t
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/t/logcdf}
*/
import logcdf from './../../../../base/dists/t/logcdf';
setReadOnly( t, 'logcdf', logcdf );

/**
* @name logpdf
* @memberof t
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/t/logpdf}
*/
import logpdf from './../../../../base/dists/t/logpdf';
setReadOnly( t, 'logpdf', logpdf );

/**
* @name mean
* @memberof t
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/t/mean}
*/
import mean from './../../../../base/dists/t/mean';
setReadOnly( t, 'mean', mean );

/**
* @name median
* @memberof t
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/t/median}
*/
import median from './../../../../base/dists/t/median';
setReadOnly( t, 'median', median );

/**
* @name mode
* @memberof t
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/t/mode}
*/
import mode from './../../../../base/dists/t/mode';
setReadOnly( t, 'mode', mode );

/**
* @name pdf
* @memberof t
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/t/pdf}
*/
import pdf from './../../../../base/dists/t/pdf';
setReadOnly( t, 'pdf', pdf );

/**
* @name quantile
* @memberof t
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/t/quantile}
*/
import quantile from './../../../../base/dists/t/quantile';
setReadOnly( t, 'quantile', quantile );

/**
* @name skewness
* @memberof t
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/t/skewness}
*/
import skewness from './../../../../base/dists/t/skewness';
setReadOnly( t, 'skewness', skewness );

/**
* @name stdev
* @memberof t
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/t/stdev}
*/
import stdev from './../../../../base/dists/t/stdev';
setReadOnly( t, 'stdev', stdev );

/**
* @name variance
* @memberof t
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/t/variance}
*/
import variance from './../../../../base/dists/t/variance';
setReadOnly( t, 'variance', variance );


// EXPORTS //

export default t;
